import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import useFormUtils from 'hooks/useFormUtils';
import { getMirroredDeclaration } from 'store/declarations/client';
import { Declaration } from 'store/declarations/declaration';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { requestOverEventBus } from 'utils/eventBusRequest';
import { Events } from 'utils/events.enum';
import { getErrorMessage } from 'utils/getErrorMessage';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { MIRRORED_DECLARATION_TARGET, MIRRORED_DECLARATIONS_PAGE_SIZE } from './constants';
import { MirroringPreparationLocationState } from './preparation-page/types';

// Saving goes through the form tab and out to the API, so it gets a longer leash than a validation
// that only has to run locally.
const SAVE_TIMEOUT_MS = 60_000;

// Every message below is written to be shown to the user as is - see `runFlowStep`.
const SAVE_FAILED_MESSAGE = 'We could not save your changes. Please save the declaration and try again.';
const MIRROR_LOOKUP_FAILED_MESSAGE =
    'We could not check whether a mirrored declaration already exists. Please try again.';
const VALIDATION_UNAVAILABLE_MESSAGE = 'We could not validate this declaration. Please try again.';

type Props = {
    declaration: Declaration | undefined;
    manifestId?: string;
    jobId?: string;
};

/**
 * Owns the flow that takes a declaration from the form to the mirroring preparation page:
 * save any pending changes, deal with a mirrored declaration that already exists, and settle the
 * master level validation before handing over. Item level errors are the preparation page's job.
 */
const useMirroringPreparation = ({ declaration, manifestId, jobId }: Props) => {
    const navigate = useNavigate();
    const { countryLowerCase, internalType, formType } = useFormUtils();
    const { declarationValidation } = useDeclarationValidation({ formAction: null });

    const [isPreparing, setIsPreparing] = useState(false);
    const [existingMirroredDeclarations, setExistingMirroredDeclarations] = useState<Declaration[]>([]);
    const [isValidationPromptOpen, setIsValidationPromptOpen] = useState(false);

    const goToPreparationPage = useCallback(() => {
        const locationState: MirroringPreparationLocationState = {
            sourceDeclaration: {
                country: countryLowerCase as DeclarationCountry,
                internalType: internalType as DeclarationInternalType,
                messageType: formType as MessageTypes,
                declarationId: declaration?.id,
            },
            mirroredDeclaration: MIRRORED_DECLARATION_TARGET,
        };

        navigate('prepare-mirroring', { state: locationState });
    }, [navigate, declaration?.id, countryLowerCase, internalType, formType]);

    const saveDeclarationAsDraft = useCallback(async () => {
        try {
            await requestOverEventBus(
                Events.TRIGGER_SAVE_DECLARATION_AS_DRAFT,
                Events.SAVING_DECLARATION_END,
                undefined,
                { timeout: SAVE_TIMEOUT_MS }
            );
        } catch (error) {
            console.error('Saving the declaration before mirroring failed', error);
            throw new Error(SAVE_FAILED_MESSAGE);
        }
    }, []);

    // The form tab owns the formik instance, so it runs the validation, highlights the fields that
    // failed and replies with the verdict - all we get back is whether it passed. A silent run
    // skips the highlighting, for when we only need the answer and not the fields.
    const validateMasterLevelForm = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
        try {
            return await requestOverEventBus<boolean>(
                Events.TRIGGER_VALIDATE_MASTER_FORM,
                Events.MASTER_FORM_VALIDATION_END,
                { silent }
            );
        } catch (error) {
            console.error('Master level validation did not answer', error);
            throw new Error(VALIDATION_UNAVAILABLE_MESSAGE);
        }
    }, []);

    const findExistingMirroredDeclarations = useCallback(async (declarationId: string) => {
        try {
            const { list } = await getMirroredDeclaration(declarationId, { size: MIRRORED_DECLARATIONS_PAGE_SIZE });
            return list;
        } catch (error) {
            console.error('Looking up the existing mirrored declaration failed', error);
            // Stopping here is the point: carrying on as if there were no mirrored declaration is
            // how a second one ends up linked to the same source.
            throw new Error(MIRROR_LOOKUP_FAILED_MESSAGE);
        }
    }, []);

    // Shared shell for every entry point into the flow: keep the button spinning for as long as the
    // step runs, and turn a failure into a notification rather than a button stuck loading. Steps
    // throw errors whose message is meant for the user.
    const runFlowStep = useCallback(async (step: () => Promise<void>) => {
        setIsPreparing(true);
        try {
            await step();
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: `Could Not Generate ${MIRRORED_DECLARATION_TARGET.messageType}`,
                description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
        } finally {
            setIsPreparing(false);
        }
    }, []);

    // Decides whether the user has to be asked about validating before the preparation page opens.
    // Reached either straight from the button, or after they chose to replace an existing mirrored
    // declaration.
    const settleMasterLevelValidation = useCallback(async () => {
        // Silent - the user has not asked for a validation yet, we are only deciding whether to
        // prompt for one. The errors are shown only once they choose to validate.
        const isValid = await validateMasterLevelForm({ silent: true });

        if (isValid) {
            goToPreparationPage();
            return;
        }

        setIsValidationPromptOpen(true);
    }, [validateMasterLevelForm, goToPreparationPage]);

    const start = useCallback(
        () =>
            runFlowStep(async () => {
                if (!declaration?.id) return;

                if (declarationValidation.hasUnsavedChanges) {
                    await saveDeclarationAsDraft();
                }

                // Checked before validating on purpose - a released mirrored declaration cannot be
                // replaced, so the modal is as far as the user gets and a validation would have
                // nothing to serve.
                const existing = await findExistingMirroredDeclarations(declaration.id);

                if (existing.length > 0) {
                    setExistingMirroredDeclarations(existing);
                    return;
                }

                await settleMasterLevelValidation();
            }),
        [
            runFlowStep,
            declaration?.id,
            declarationValidation.hasUnsavedChanges,
            saveDeclarationAsDraft,
            findExistingMirroredDeclarations,
            settleMasterLevelValidation,
        ]
    );

    // Nothing is unlinked or deleted here. Generating on the preparation page replaces the whole
    // existing set in one request, so the BE either swaps all of it or leaves it untouched.
    const replaceExistingMirroredDeclaration = useCallback(
        () =>
            runFlowStep(async () => {
                setExistingMirroredDeclarations([]);

                await settleMasterLevelValidation();
            }),
        [runFlowStep, settleMasterLevelValidation]
    );

    // Only offered when a single mirrored declaration exists - with several, the modal links to each.
    const viewExistingMirroredDeclaration = useCallback(() => {
        const [firstExistingDeclaration] = existingMirroredDeclarations;
        if (!firstExistingDeclaration?.id) return;

        setExistingMirroredDeclarations([]);
        navigate(getDeclarationUrl({ manifestId, jobId, declarationId: firstExistingDeclaration.id }));
    }, [existingMirroredDeclarations, navigate, manifestId, jobId]);

    // Failing the validation keeps the user on the form with the offending fields highlighted -
    // master level errors are deliberately not fixable on the preparation page.
    const validateThenContinue = useCallback(
        () =>
            runFlowStep(async () => {
                setIsValidationPromptOpen(false);

                if (await validateMasterLevelForm()) {
                    goToPreparationPage();
                    return;
                }

                // Not a failure of the flow - the declaration simply is not ready, and the fields
                // that say why have just been highlighted on the form behind the prompt.
                Notification({
                    type: 'error',
                    messageTitle: 'Validation Failed',
                    description: `Resolve the highlighted errors before generating the ${MIRRORED_DECLARATION_TARGET.messageType}.`,
                    placement: 'top',
                });
            }),
        [runFlowStep, validateMasterLevelForm, goToPreparationPage]
    );

    // Goes on with the master level errors as they are, to be resolved on the generated declaration.
    const skipValidationAndContinue = useCallback(() => {
        setIsValidationPromptOpen(false);
        goToPreparationPage();
    }, [goToPreparationPage]);

    return {
        isPreparing,
        start,
        existingMirroredDeclarations,
        replaceExistingMirroredDeclaration,
        viewExistingMirroredDeclaration,
        dismissExistingMirroredDeclaration: useCallback(() => setExistingMirroredDeclarations([]), []),
        isValidationPromptOpen,
        validateThenContinue,
        skipValidationAndContinue,
        dismissValidationPrompt: useCallback(() => setIsValidationPromptOpen(false), []),
    };
};

export default useMirroringPreparation;
